import { ChatComposer, ChatStream, useChat, type ChatComposerHandle, type ChatLocationState } from '@components/Chat'
import { cardRoute, cardRoutePath, type ChatCard } from '@components/Chat/core'
import { ErrorNote } from '@components/Notice'
import { PageHeader, PanePageColumn, PaneScroll } from '@components/Page'
import PageLoader from '@components/PageLoader'
import { useMe, useTrip } from '@hooks'
import React, { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ChatPanel } from './ChatPage.styles'

interface ChatScreenProps {
	tripId: string
	myUserId: number
	tripName: string | undefined
}

/**
 * One trip's Chat: the stream of Messages over the composer. State and server work live
 * in `useChat`, the scrolling in `ChatStream`; this lays them out, opens on the Message
 * `?message=` names, and takes the card "Chat about this" sends. Ported from iOS `ChatVC`.
 */
const ChatScreen: React.FC<ChatScreenProps> = ({ tripId, myUserId, tripName }) => {
	const navigate = useNavigate()
	const location = useLocation()
	const [params] = useSearchParams()
	const revealParam = Number(params.get('message'))
	const chat = useChat(tripId, myUserId, Number.isInteger(revealParam) && revealParam > 0 ? revealParam : null)
	const composerRef = useRef<ChatComposerHandle>(null)

	// "Chat about this" on a detail surface: the card joins the Message being written,
	// its text and other tags kept, and the field takes focus. Taken once.
	const chatAbout = (location.state as ChatLocationState | null)?.chatAbout
	useEffect(() => {
		if (!chatAbout) return
		composerRef.current?.attach(chatAbout)
		composerRef.current?.focus()
		navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
	}, [chatAbout, location.pathname, location.search, navigate])

	const openCard = (card: ChatCard) => {
		const route = cardRoute(card)
		if (route) navigate(cardRoutePath(tripId, route))
	}

	return (
		<PaneScroll>
			<PanePageColumn>
				<PageHeader title="Chat" backTo={{ path: `/trips/${tripId}`, label: tripName ?? 'Trip' }} />
				<ChatPanel>
					<ChatStream
						items={chat.items}
						lastUpdate={chat.lastUpdate}
						hasLoaded={chat.hasLoaded}
						emptyState={chat.emptyState}
						onNearTop={chat.actions.loadOlder}
						onMessagesShown={chat.actions.messagesShown}
						onOpenCard={openCard}
						onOpenQuote={chat.actions.reveal}
						onReact={chat.actions.toggleReaction}
						onAddLinkAsIdea={chat.actions.addLinkAsIdea}
						onReply={messageId => {
							const quote = chat.quoteOf(messageId)
							if (quote) composerRef.current?.reply(quote)
						}}
						onDelete={chat.actions.delete}
						onRetry={chat.actions.retry}
						onDiscard={chat.actions.discard}
					/>
					<ChatComposer ref={composerRef} tripId={tripId} myUserId={myUserId} onSend={chat.actions.send} />
				</ChatPanel>
			</PanePageColumn>
		</PaneScroll>
	)
}

/** The trip's Chat at `/trips/:tripId/chat`, mounted afresh for each trip. */
const ChatPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const { data: trip, isLoading: tripLoading } = useTrip(tripId)
	const { data: me } = useMe()

	if (!tripId) return <ErrorNote>Trip not found</ErrorNote>
	if ((tripLoading && !trip) || !me) return <PageLoader label="Loading the chat…" />
	return <ChatScreen key={tripId} tripId={tripId} myUserId={me.id} tripName={trip?.name} />
}

export default ChatPage
